/**
 * Profile Gallery Page
 * Manage photos and videos: upload, reorder, remove, and embed links
 * Supports YouTube, Vimeo, Instagram and local file uploads
 *
 * @author Varun Pandey
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Image as ImageIcon, ArrowLeft, Plus, Link2, X, Play, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { FileUpload, UploadedFile } from '../../components/ui/FileUpload';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { cn } from '../../utils/cn';
import { ArtistService, type GalleryImageItem, type GalleryVideoItem } from '../../services/artistService';
import * as YouTube from '../../services/youtubeService';
import * as Vimeo from '../../services/vimeoService';
import { isAllowedVideoUrl } from '../../services/noembedService';
import { isNativeIOSApp } from '../../utils/platform';
import toast from 'react-hot-toast';
import { InstagramAuthCore } from '@/instagram/InstagramAuthCore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { useInstagramStore } from '@/stores/instagramStore';
import { fetchInstagramMediaPage, fetchInstagramVideosPage } from '@/services/instagramMediaService';
import { Loader } from '@/components/ui';
import {
  ProfileCompletionBlocksControl,
  ProfileIncompleteCornerPing,
} from '@/components/profile/ProfileCompletionBlocksControl';

// Base origin used to load the YouTube proxy page on native iOS. The native
// WKWebView runs on https://localhost and sends no valid Referer, so YouTube
// embeds fail with Error 153. Loading the embed from a real HTTPS page fixes it.
const YT_PROXY_BASE = (import.meta.env.VITE_URL || 'https://artist.starclinch.com').replace(/\/$/, '');

type VideoSource = 'instagram' | 'youtube' | 'vimeo' | 'link';

interface VideoSearchResult {
  id: string;
  title: string;
  platform: 'youtube' | 'vimeo';
  url: string;
  selected?: boolean;
  thumbnailUrl?: string;
}

export const ProfileGalleryPage: React.FC = () => {
  const INSTAGRAM_GALLERY_RETURN_STATE_KEY = 'profileGalleryInstagramReturnState';
  const navigate = useNavigate();
  const { data: artistDetails, fetchArtistDetails } = useArtistDetailsStore();

  const { galleryImagesIncomplete, galleryVideosIncomplete } = useMemo(() => {
    const raw = artistDetails?.incompleted_sections;
    if (!Array.isArray(raw)) {
      return { galleryImagesIncomplete: false, galleryVideosIncomplete: false };
    }
    const set = new Set(raw);
    return {
      galleryImagesIncomplete: set.has('gallery_images'),
      galleryVideosIncomplete: set.has('gallery_videos'),
    };
  }, [artistDetails?.incompleted_sections]);
  const instagramConnection = useInstagramStore((s) => s.connection);
  // Gallery images from API (id, order, url)
  const [galleryImages, setGalleryImages] = useState<GalleryImageItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [pendingPhotos, setPendingPhotos] = useState<UploadedFile[]>([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const [isModifyMode, setIsModifyMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Videos state – from API
  const [galleryVideos, setGalleryVideos] = useState<GalleryVideoItem[]>([]);
  const [galleryVideosLoading, setGalleryVideosLoading] = useState(true);
  const [videoModifyMode, setVideoModifyMode] = useState(false);
  const galleryVideosFetchedRef = useRef(false);

  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [activeSource, setActiveSource] = useState<VideoSource>('link');
  const [youtubeQuery, setYoutubeQuery] = useState('');
  const [youtubeChannels, setYoutubeChannels] = useState<YouTube.YouTubeChannel[]>([]);
  const [selectedYouTubeChannelId, setSelectedYouTubeChannelId] = useState<string | null>(null);
  const [youtubeChannelLoadingId, setYoutubeChannelLoadingId] = useState<string | null>(null);
  const [youtubeResults, setYoutubeResults] = useState<VideoSearchResult[]>([]);
  const [youtubeChannelPages, setYoutubeChannelPages] = useState<YouTube.YouTubeChannel[][]>([]);
  const [youtubeChannelPageTokens, setYoutubeChannelPageTokens] = useState<(string | null)[]>([]);
  const [youtubeChannelPage, setYoutubeChannelPage] = useState(1);
  const [youtubeVideoPages, setYoutubeVideoPages] = useState<VideoSearchResult[][]>([]);
  const [youtubeVideoPageTokens, setYoutubeVideoPageTokens] = useState<(string | null)[]>([]);
  const [youtubeVideoPage, setYoutubeVideoPage] = useState(1);
  const [vimeoQuery, setVimeoQuery] = useState('');
  const [vimeoResults, setVimeoResults] = useState<VideoSearchResult[]>([]);
  const [isVideoEditorOpen, setIsVideoEditorOpen] = useState(false);
  const [videoSearchLoading, setVideoSearchLoading] = useState(false);
  const [instagramVideos, setInstagramVideos] = useState<string[]>([]);
  const [selectedInstagramVideos, setSelectedInstagramVideos] = useState<Set<string>>(new Set());
  const [isLoadingInstagramVideos, setIsLoadingInstagramVideos] = useState(false);

  // Photo Editor state
  const [isPhotoEditorOpen, setIsPhotoEditorOpen] = useState(false);
  const [activePhotoSource, setActivePhotoSource] = useState<'device' | 'instagram'>('device');
  const [instagramPhotos, setInstagramPhotos] = useState<string[]>([]);
  const [instagramPhotosNextCursor, setInstagramPhotosNextCursor] = useState<string | null>(null);
  const [selectedInstagramPhotos, setSelectedInstagramPhotos] = useState<Set<string>>(new Set());
  const [isLoadingInstagramPhotos, setIsLoadingInstagramPhotos] = useState(false);
  const [isLoadingMoreInstagramPhotos, setIsLoadingMoreInstagramPhotos] = useState(false);

  const galleryFetchedRef = useRef(false);

  const instagramConnected = instagramConnection?.connected === true;

  const saveInstagramReturnState = (target: 'photos' | 'videos') => {
    if (typeof window === 'undefined') return;
    const state = {
      target,
      isPhotoEditorOpen,
      activePhotoSource,
      isVideoEditorOpen,
      activeSource,
      savedAt: Date.now(),
    };
    window.sessionStorage.setItem(
      INSTAGRAM_GALLERY_RETURN_STATE_KEY,
      JSON.stringify(state),
    );
  };

  useEffect(() => {
    if (galleryFetchedRef.current) return;
    galleryFetchedRef.current = true;
    setGalleryLoading(true);
    ArtistService.getGalleryImages()
      .then((data) => {
        setGalleryImages(data);
        void fetchArtistDetails();
      })
      .catch(() => toast.error('Failed to load gallery'))
      .finally(() => setGalleryLoading(false));
  }, [fetchArtistDetails]);

  useEffect(() => {
    if (galleryVideosFetchedRef.current) return;
    galleryVideosFetchedRef.current = true;
    setGalleryVideosLoading(true);
    ArtistService.getGalleryVideos()
      .then((data) => {
        setGalleryVideos(data);
        void fetchArtistDetails();
      })
      .catch(() => toast.error('Failed to load videos'))
      .finally(() => setGalleryVideosLoading(false));
  }, [fetchArtistDetails]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rawState = window.sessionStorage.getItem(INSTAGRAM_GALLERY_RETURN_STATE_KEY);
    if (!rawState) return;

    window.sessionStorage.removeItem(INSTAGRAM_GALLERY_RETURN_STATE_KEY);
    try {
      const parsed = JSON.parse(rawState) as {
        target?: 'photos' | 'videos';
        isPhotoEditorOpen?: boolean;
        activePhotoSource?: 'device' | 'instagram';
        isVideoEditorOpen?: boolean;
        activeSource?: VideoSource;
      };

      if (parsed.target === 'videos') {
        setIsVideoEditorOpen(true);
        setActiveSource('instagram');
      } else if (parsed.target === 'photos') {
        setIsPhotoEditorOpen(true);
        setActivePhotoSource('instagram');
      } else {
        setIsPhotoEditorOpen(Boolean(parsed.isPhotoEditorOpen));
        setIsVideoEditorOpen(Boolean(parsed.isVideoEditorOpen));
        if (parsed.activePhotoSource) setActivePhotoSource(parsed.activePhotoSource);
        if (parsed.activeSource) setActiveSource(parsed.activeSource);
      }
    } catch {
      // Ignore malformed state and continue with defaults.
    }
  }, []);

  useEffect(() => {
    if (!instagramConnected) return;
    if (isVideoEditorOpen && activeSource === 'instagram' && instagramVideos.length === 0 && !isLoadingInstagramVideos) {
      void loadInstagramVideos(false);
    }
  }, [
    instagramConnected,
    isVideoEditorOpen,
    activeSource,
    instagramVideos.length,
    isLoadingInstagramVideos,
  ]);

  useEffect(() => {
    if (!instagramConnected) return;
    if (
      isPhotoEditorOpen &&
      activePhotoSource === 'instagram' &&
      instagramPhotos.length === 0 &&
      !isLoadingInstagramPhotos
    ) {
      void handleLoadInstagramPhotos();
    }
  }, [
    instagramConnected,
    isPhotoEditorOpen,
    activePhotoSource,
    instagramPhotos.length,
    isLoadingInstagramPhotos,
  ]);

  // Video Handlers – API-backed
  const handleAddVideoLink = async () => {
    const url = newVideoUrl.trim();
    if (!url) return;
    if (!isAllowedVideoUrl(url)) {
      toast.error('Only YouTube and Vimeo links are allowed');
      return;
    }
    setIsSubmitting(true);
    try {
      await ArtistService.addGalleryVideos([url]);
      const data = await ArtistService.getGalleryVideos();
      setGalleryVideos(data);
      await fetchArtistDetails();
      setNewVideoUrl('');
      setIsVideoEditorOpen(false);
      toast.success('Video added');
    } catch (err: any) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to add video';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearchYoutube = async () => {
    if (!youtubeQuery.trim()) return;
    setVideoSearchLoading(true);
    setSelectedYouTubeChannelId(null);
    setYoutubeResults([]);
    setYoutubeChannelPages([]);
    setYoutubeChannelPageTokens([]);
    setYoutubeChannelPage(1);
    setYoutubeVideoPages([]);
    setYoutubeVideoPageTokens([]);
    setYoutubeVideoPage(1);
    try {
      const { channels, nextPageToken } = await YouTube.searchChannels(youtubeQuery);
      setYoutubeChannelPages([channels]);
      setYoutubeChannelPageTokens([nextPageToken ?? null]);
      setYoutubeChannelPage(1);
      setYoutubeChannels(channels);
    } catch {
      toast.error('YouTube search failed. Check your API key.');
    } finally {
      setVideoSearchLoading(false);
    }
  };

  const handleSelectYouTubeChannel = async (channelId: string) => {
    setVideoSearchLoading(true);
    setYoutubeChannelLoadingId(channelId);
    setYoutubeVideoPages([]);
    setYoutubeVideoPageTokens([]);
    setYoutubeVideoPage(1);
    try {
      const { videos, nextPageToken } = await YouTube.getChannelVideos(channelId);
      const mapped: VideoSearchResult[] = videos.map((v) => ({
        id: v.videoId,
        title: v.title,
        url: v.url,
        platform: 'youtube' as const,
        selected: false,
        thumbnailUrl: YouTube.getYouTubeThumbUrl(v.thumbnails ?? ({} as any)),
      }));
      setYoutubeVideoPages([mapped]);
      setYoutubeVideoPageTokens([nextPageToken ?? null]);
      setYoutubeVideoPage(1);
      setYoutubeResults(mapped);
      setSelectedYouTubeChannelId(channelId);
    } catch {
      toast.error('Failed to load channel videos');
    } finally {
      setYoutubeChannelLoadingId(null);
      setVideoSearchLoading(false);
    }
  };

  const handleSearchVimeo = async () => {
    if (!vimeoQuery.trim()) return;
    setVideoSearchLoading(true);
    try {
      const videos = await Vimeo.searchVimeoVideos(vimeoQuery);
      setVimeoResults(
        videos.map((v) => ({
          id: v.id,
          title: v.name,
          url: v.url,
          platform: 'vimeo' as const,
          selected: false,
          thumbnailUrl: v.thumbnail,
        }))
      );
    } catch {
      toast.error('Vimeo search failed. Check your access token.');
    } finally {
      setVideoSearchLoading(false);
    }
  };

  const toggleResultSelection = (platform: 'youtube' | 'vimeo', id: string) => {
    const updater =
      platform === 'youtube' ? setYoutubeResults : setVimeoResults;
    const current = platform === 'youtube' ? youtubeResults : vimeoResults;
    updater(
      current.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleAddSelectedFromResults = async (platform: 'youtube' | 'vimeo') => {
    const current = platform === 'youtube' ? youtubeResults : vimeoResults;
    const selected = current.filter((item) => item.selected);
    if (selected.length === 0) {
      toast.error('Select at least one video');
      return;
    }
    const urls = selected.map((item) => item.url);
    setIsSubmitting(true);
    try {
      await ArtistService.addGalleryVideos(urls);
      const data = await ArtistService.getGalleryVideos();
      setGalleryVideos(data);
      await fetchArtistDetails();
      if (platform === 'youtube') {
        setYoutubeResults((prev) => prev.map((r) => ({ ...r, selected: false })));
        setSelectedYouTubeChannelId(null);
        setYoutubeChannels([]);
      } else {
        setVimeoResults((prev) => prev.map((r) => ({ ...r, selected: false })));
      }
      setIsVideoEditorOpen(false);
      toast.success('Videos added');
    } catch (err: any) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to add videos';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleYouTubeChannelsPrevPage = () => {
    if (youtubeChannelPage <= 1) return;
    const newPage = youtubeChannelPage - 1;
    setYoutubeChannelPage(newPage);
    const prevPageChannels = youtubeChannelPages[newPage - 1] ?? [];
    setYoutubeChannels(prevPageChannels);
  };

  const handleYouTubeChannelsNextPage = async () => {
    const currentIndex = youtubeChannelPage - 1;
    const nextToken = youtubeChannelPageTokens[currentIndex];
    if (!nextToken) return;

    // If next page already loaded, just switch to it
    if (youtubeChannelPages[currentIndex + 1]) {
      const newPage = youtubeChannelPage + 1;
      setYoutubeChannelPage(newPage);
      setYoutubeChannels(youtubeChannelPages[currentIndex + 1]);
      return;
    }

    setVideoSearchLoading(true);
    try {
      const { channels, nextPageToken } = await YouTube.searchChannels(
        youtubeQuery,
        nextToken || undefined,
      );
      setYoutubeChannelPages((prev) => [...prev, channels]);
      setYoutubeChannelPageTokens((prev) => [...prev, nextPageToken ?? null]);
      const newPage = youtubeChannelPage + 1;
      setYoutubeChannelPage(newPage);
      setYoutubeChannels(channels);
    } catch {
      toast.error('YouTube search failed. Please try again.');
    } finally {
      setVideoSearchLoading(false);
    }
  };

  const handleYouTubeVideosPrevPage = () => {
    if (youtubeVideoPage <= 1) return;
    const newPage = youtubeVideoPage - 1;
    setYoutubeVideoPage(newPage);
    const prevPageVideos = youtubeVideoPages[newPage - 1] ?? [];
    setYoutubeResults(prevPageVideos);
  };

  const handleYouTubeVideosNextPage = async () => {
    if (!selectedYouTubeChannelId) return;
    const currentIndex = youtubeVideoPage - 1;
    const nextToken = youtubeVideoPageTokens[currentIndex];
    if (!nextToken) return;

    if (youtubeVideoPages[currentIndex + 1]) {
      const newPage = youtubeVideoPage + 1;
      setYoutubeVideoPage(newPage);
      setYoutubeResults(youtubeVideoPages[currentIndex + 1]);
      return;
    }

    setVideoSearchLoading(true);
    try {
      const { videos, nextPageToken } = await YouTube.getChannelVideos(
        selectedYouTubeChannelId,
        nextToken || undefined,
      );
      const mapped: VideoSearchResult[] = videos.map((v) => ({
        id: v.videoId,
        title: v.title,
        url: v.url,
        platform: 'youtube' as const,
        selected: false,
        thumbnailUrl: YouTube.getYouTubeThumbUrl(v.thumbnails ?? ({} as any)),
      }));
      setYoutubeVideoPages((prev) => [...prev, mapped]);
      setYoutubeVideoPageTokens((prev) => [...prev, nextPageToken ?? null]);
      const newPage = youtubeVideoPage + 1;
      setYoutubeVideoPage(newPage);
      setYoutubeResults(mapped);
    } catch {
      toast.error('Failed to load more videos');
    } finally {
      setVideoSearchLoading(false);
    }
  };

  const handleDeleteGalleryVideo = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSubmitting(true);
    try {
      await ArtistService.deleteGalleryVideo(id);
      const data = await ArtistService.getGalleryVideos();
      setGalleryVideos(data);
      await fetchArtistDetails();
      if (data.length === 0) {
        setVideoModifyMode(false);
      }
      toast.success('Video removed');
    } catch {
      toast.error('Failed to remove video');
    } finally {
      setIsSubmitting(false);
    }
  };

  const allPhotoUrls = galleryImages.map((img) => img.url);
  const showLightbox = activePhotoIndex !== null && allPhotoUrls[activePhotoIndex] != null;

  const handleAddPendingPhotos = async () => {
    if (pendingPhotos.length === 0) return;
    const formData = new FormData();
    pendingPhotos.forEach((p) => formData.append('images', p.file));
    setIsSubmitting(true);
    try {
      await ArtistService.addGalleryImages(formData);
      const data = await ArtistService.getGalleryImages();
      setGalleryImages(data);
      await fetchArtistDetails();
      setPendingPhotos([]);
      setIsPhotoEditorOpen(false);
      toast.success('Photos added');
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to add photos';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGalleryImage = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSubmitting(true);
    try {
      await ArtistService.deleteGalleryImage(id);
      const data = await ArtistService.getGalleryImages();
      setGalleryImages(data);
      await fetchArtistDetails();
      if (data.length === 0) {
        setIsModifyMode(false);
      }
      if (activePhotoIndex !== null) setActivePhotoIndex(null);
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextPhoto = () => {
    if (!showLightbox || activePhotoIndex === null || allPhotoUrls.length === 0) return;
    setActivePhotoIndex((activePhotoIndex + 1) % allPhotoUrls.length);
  };

  const handlePrevPhoto = () => {
    if (!showLightbox || activePhotoIndex === null || allPhotoUrls.length === 0) return;
    setActivePhotoIndex(
      (activePhotoIndex - 1 + allPhotoUrls.length) % allPhotoUrls.length,
    );
  };

  const getVideoKind = (url: string): 'youtube' | 'vimeo' | 'html5' => {
    if (/youtube\.com|youtu\.be/.test(url)) return 'youtube';
    if (/vimeo\.com/.test(url)) return 'vimeo';
    return 'html5';
  };

  const getEmbedUrl = (url: string): string => {
    const kind = getVideoKind(url);
    if (kind === 'youtube') {
      const idMatch =
        url.match(/[?&]v=([^&]+)/) ||
        url.match(/youtu\.be\/([^?]+)/) ||
        url.match(/youtube\.com\/embed\/([^?]+)/) ||
        url.match(/youtube\.com\/shorts\/([^/?]+)/) ||
        url.match(/m\.youtube\.com\/shorts\/([^/?]+)/);
      const id = idMatch?.[1] ?? '';
      if (!id) return url;
      // On native iOS, route through the HTTPS proxy page to avoid YouTube Error 153.
      return isNativeIOSApp()
        ? `${YT_PROXY_BASE}/embed/youtube/${id}`
        : `https://www.youtube.com/embed/${id}`;
    }
    if (kind === 'vimeo') {
      const idMatch = url.match(/vimeo\.com\/(\d+)/);
      const id = idMatch?.[1] ?? '';
      return id ? `https://player.vimeo.com/video/${id}` : url;
    }
    return url;
  };

  const handleToggleInstagramPhoto = (url: string) => {
    setSelectedInstagramPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const handleImportSelectedInstagramPhotos = async () => {
    if (selectedInstagramPhotos.size === 0) {
      toast.error('Select at least one Instagram photo to import.');
      return;
    }
    const media_links = Array.from(selectedInstagramPhotos);
    setIsSubmitting(true);
    try {
      // Hit the photos API with media_links payload, mirroring videos import
      await ArtistService.addGalleryImagesFromLinks(media_links);
      const data = await ArtistService.getGalleryImages();
      setGalleryImages(data);
      await fetchArtistDetails();
      setSelectedInstagramPhotos(new Set());
      setIsPhotoEditorOpen(false);
      setActivePhotoSource('device');
      toast.success('Instagram photos imported');
    } catch (err: any) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to import Instagram photos';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadInstagramPhotos = async () => {
    if (!instagramConnected) return;
    setIsLoadingInstagramPhotos(true);
    setInstagramPhotosNextCursor(null);
    try {
      const result = await fetchInstagramMediaPage(20);
      const links = result.media.map((m) => m.media_url);
      setInstagramPhotos(links);
      setInstagramPhotosNextCursor(result.nextCursor);
      if (links.length === 0) {
        toast('No Instagram photos available to import yet.');
      } else {
        toast.success('Instagram photos ready to import');
      }
    } catch (err) {
      console.error('Instagram media fetch error (photos):', err);
      toast.error('Unable to load Instagram photos. Please reconnect Instagram from Gallery.');
    } finally {
      setIsLoadingInstagramPhotos(false);
    }
  };

  const handleLoadMoreInstagramPhotos = async () => {
    if (!instagramPhotosNextCursor || isLoadingMoreInstagramPhotos) return;
    setIsLoadingMoreInstagramPhotos(true);
    try {
      const result = await fetchInstagramMediaPage(20, instagramPhotosNextCursor);
      const newUrls = result.media.map((m) => m.media_url);
      setInstagramPhotos((prev) => [...prev, ...newUrls]);
      setInstagramPhotosNextCursor(result.nextCursor);
      if (newUrls.length > 0) {
        toast.success(`Loaded ${newUrls.length} more photos`);
      }
    } catch (err) {
      console.error('Instagram media load more error:', err);
      toast.error('Failed to load more photos.');
    } finally {
      setIsLoadingMoreInstagramPhotos(false);
    }
  };

  const handleToggleInstagramVideo = (url: string) => {
    setSelectedInstagramVideos((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const handleImportSelectedInstagramVideos = async () => {
    if (selectedInstagramVideos.size === 0) {
      toast.error('Select at least one Instagram video to import.');
      return;
    }
    const media_links = Array.from(selectedInstagramVideos);
    setIsSubmitting(true);
    try {
      await ArtistService.addGalleryVideos(media_links);
      const data = await ArtistService.getGalleryVideos();
      setGalleryVideos(data);
      await fetchArtistDetails();
      setSelectedInstagramVideos(new Set());
      setIsVideoEditorOpen(false);
      setActiveSource('link');
      toast.success('Instagram videos imported');
    } catch (err: any) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to import Instagram videos';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadInstagramVideos = async (showToast: boolean) => {
    if (!instagramConnected) return;
    setIsLoadingInstagramVideos(true);
    try {
      // Fetch directly from Instagram Graph API (videos only) using stored token
      const result = await fetchInstagramVideosPage(20);
      const links = result.media.map((m) => m.media_url);
      setInstagramVideos(links);
      if (!showToast) return;
      if (links.length === 0) {
        toast('No Instagram videos available to import yet.');
      } else {
        toast.success('Instagram videos ready to import');
      }
    } catch (err) {
      console.error('Instagram media fetch error (videos):', err);
      if (showToast) {
        toast.error('Unable to load Instagram videos. Please reconnect Instagram from Gallery.');
      }
    } finally {
      setIsLoadingInstagramVideos(false);
    }
  };

  const handleLoadInstagramVideos = async () => {
    await loadInstagramVideos(true);
  };

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        {/* Header */}
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
              <ImageIcon className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                Gallery
              </h1>
              <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                Showcase your best photos and videos
              </p>
            </div>
          </div>
        </header>

        <div className="space-y-6 lg:space-y-8 mb-16">
          {/* Photos */}
          <section
            className={cn(
              'relative overflow-hidden rounded-2xl border p-5 lg:p-6 bg-[#1C1C1E]',
              galleryImagesIncomplete
                ? 'border-amber-400/25 shadow-[inset_0_1px_0_0_rgba(251,191,36,0.12)] hover:border-amber-400/30'
                : 'border-[#3A3A3C]'
            )}
          >
            {galleryImagesIncomplete ? (
              <div
                className="pointer-events-none absolute inset-x-0 top-0 z-[1]"
                aria-hidden
              >
                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/45 to-transparent" />
                <ProfileIncompleteCornerPing />
              </div>
            ) : null}
            <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-base lg:text-lg font-semibold text-white">Photos</h2>
                <p className="text-xs lg:text-sm text-[#8E8E93] mt-0.5">
                  Upload high-quality images and import directly from Instagram.
                </p>
              </div>
              <div className="inline-flex items-center gap-2">
                {galleryImages.length > 0 && !isPhotoEditorOpen && (
                  <button
                    type="button"
                    onClick={() => setIsModifyMode((prev) => !prev)}
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                  >
                    {isModifyMode ? 'Done' : 'Modify'}
                  </button>
                )}
                {!isModifyMode && (
                  <button
                    type="button"
                    onClick={() => setIsPhotoEditorOpen((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {isPhotoEditorOpen ? 'Close add photos' : 'Add photos'}
                  </button>
                )}
              </div>
            </div>

            {/* Photo Editor */}
            <AnimatePresence initial={false}>
              {isPhotoEditorOpen && (
                <motion.div
                  key="photo-editor"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {/* Source selector */}
                  <div className="inline-flex rounded-full  p-1 text-xs gap-2">
                    {[
                      { id: 'device', label: 'Device' },
                      { id: 'instagram', label: 'Instagram' },
                    ].map((source) => (
                      <button
                        key={source.id}
                        type="button"
                        onClick={() => {
                          const nextSource = source.id as 'device' | 'instagram';
                          setActivePhotoSource(nextSource);
                          if (
                            nextSource === 'instagram' &&
                            instagramConnected &&
                            instagramPhotos.length === 0 &&
                            !isLoadingInstagramPhotos
                          ) {
                            void handleLoadInstagramPhotos();
                          }
                        }}
                        className={cn(
                          'px-3 py-1.5 rounded-full transition-colors',
                          activePhotoSource === source.id
                            ? 'bg-[#F33363] text-white'
                            : 'text-[#F2F2F7] hover:bg-[#2C2C2E]',
                        )}
                      >
                        {source.label}
                      </button>
                    ))}
                  </div>

                  <div className="rounded-2xl bg-[#151517] border border-[#3A3A3C] px-4 py-4 lg:px-5 lg:py-5 space-y-3">
                    {activePhotoSource === 'device' && (
                      <>
                        <FileUpload
                          label="Upload from device"
                          files={pendingPhotos}
                          onChange={setPendingPhotos}
                          accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] }}
                        />
                        {pendingPhotos.length > 0 && (
                          <div className="flex justify-end mt-2">
                            <Button
                              type="button"
                              variant="active"
                              onClick={handleAddPendingPhotos}
                              disabled={isSubmitting}
                              className="px-6 py-2"
                            >
                              {isSubmitting ? 'Adding…' : `Add Photo${pendingPhotos.length > 1 ? 's' : ''}`}
                            </Button>
                          </div>
                        )}
                      </>
                    )}

                    {activePhotoSource === 'instagram' && (
                      <>
                        <p className="text-sm font-semibold text-white">Import from Instagram</p>
                        <p className="text-xs text-[#8E8E93]">
                          {instagramConnected
                            ? 'Your Instagram account is connected. Your latest Instagram photos will appear here.'
                            : 'If your account is connected, your latest Instagram photos will appear here.'}
                        </p>
                        {!instagramConnected && (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              className="inline-flex items-center gap-2 px-4 py-2 text-xs lg:text-sm"
                              onClick={async () => {
                                try {
                                  saveInstagramReturnState('photos');
                                  await InstagramAuthCore.startOAuth('photos', {
                                    returnPath: '/profile/gallery',
                                  });
                                } catch (err) {
                                  console.error('Instagram photos OAuth error:', err);
                                  toast.error('Unable to start Instagram import. Please try again.');
                                }
                              }}
                              disabled={isSubmitting || isLoadingInstagramPhotos}
                            >
                              Connect Instagram
                            </Button>
                          </div>
                        )}

                        {instagramPhotos.length > 0 && (
                          <div className="mt-4 space-y-3">
                            <p className="text-xs text-[#8E8E93] uppercase tracking-wider font-medium">
                              Select photos to import
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                              {instagramPhotos.map((url) => {
                                const selected = selectedInstagramPhotos.has(url);
                                return (
                                  <button
                                    key={url}
                                    type="button"
                                    onClick={() => handleToggleInstagramPhoto(url)}
                                    className={cn(
                                      'relative aspect-square rounded-xl overflow-hidden border transition-all',
                                      selected
                                        ? 'border-[#F33363] ring-2 ring-[#F33363]/40'
                                        : 'border-[#3A3A3C]',
                                    )}
                                  >
                                    <img
                                      src={url}
                                      alt="Instagram"
                                      className="h-full w-full object-cover"
                                    />
                                    {selected && (
                                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                        <span className="text-xs font-semibold text-white">
                                          Selected
                                        </span>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            {instagramPhotosNextCursor && (
                              <div className="flex justify-center pt-2">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  className="px-4 py-2 text-sm"
                                  onClick={handleLoadMoreInstagramPhotos}
                                  disabled={isLoadingMoreInstagramPhotos}
                                >
                                  {isLoadingMoreInstagramPhotos ? 'Loading…' : 'Load more'}
                                </Button>
                              </div>
                            )}
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="active"
                                className="px-6 py-2"
                                onClick={handleImportSelectedInstagramPhotos}
                                disabled={isSubmitting || selectedInstagramPhotos.size === 0}
                              >
                                {isSubmitting ? 'Importing…' : 'Import selected'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Gallery Images Grid - hidden when Add photos panel is open */}
            {!isPhotoEditorOpen && (
              galleryLoading ? (
                <div className="mt-4 pt-4 border-t border-[#3A3A3C]">
                  <p className="text-xs text-[#8E8E93]">Loading gallery…</p>
                </div>
              ) : galleryImages.length > 0 ? (
                <div className="mt-4 pt-4 border-t border-[#3A3A3C]">
                  <p className="mb-3 text-xs text-[#8E8E93] uppercase tracking-wider font-medium">Photos in Gallery</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {galleryImages.map((img, index) => (
                      <div
                        key={img.id}
                        className="relative group aspect-square rounded-xl bg-[#2C2C2E] overflow-hidden border border-[#3A3A3C]"
                      >
                        <button
                          type="button"
                          onClick={() => setActivePhotoIndex(index)}
                          className="w-full h-full block"
                        >
                          <img
                            src={img.url}
                            alt={`Gallery photo ${index + 1}`}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </button>
                        {isModifyMode && (
                          <button
                            type="button"
                            onClick={(e) => handleDeleteGalleryImage(img.id, e)}
                            disabled={isSubmitting}
                            className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-[#FF453A] hover:text-white transition-colors z-10"
                            title="Remove photo"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null
            )}
            </div>
          </section>

          {/* Videos */}
          <section
            className={cn(
              'relative overflow-hidden rounded-2xl border p-5 lg:p-6 bg-[#1C1C1E]',
              galleryVideosIncomplete
                ? 'border-amber-400/25 shadow-[inset_0_1px_0_0_rgba(251,191,36,0.12)] hover:border-amber-400/30'
                : 'border-[#3A3A3C]'
            )}
          >
            {galleryVideosIncomplete ? (
              <div
                className="pointer-events-none absolute inset-x-0 top-0 z-[1]"
                aria-hidden
              >
                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/45 to-transparent" />
                <ProfileIncompleteCornerPing />
              </div>
            ) : null}
            <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-base lg:text-lg font-semibold text-white">Videos</h2>
                <p className="text-xs lg:text-sm text-[#8E8E93] mt-0.5">
                  Add performance videos from YouTube, Vimeo, or direct links.
                </p>
              </div>
              <div className="inline-flex items-center gap-2">
                {galleryVideos.length > 0 && !isVideoEditorOpen && (
                  <button
                    type="button"
                    onClick={() => setVideoModifyMode((prev) => !prev)}
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                  >
                    {videoModifyMode ? 'Done' : 'Modify'}
                  </button>
                )}
                {!videoModifyMode && (
                  <button
                    type="button"
                    onClick={() => setIsVideoEditorOpen((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {isVideoEditorOpen ? 'Close add videos' : 'Add videos'}
                  </button>
                )}
              </div>
            </div>

            {/* Source selector + editors – only when user opens "Add videos" */}
            <AnimatePresence initial={false}>
              {isVideoEditorOpen && (
                <motion.div
                  key="video-editor"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {/* Source selector – YouTube, Vimeo, Direct link only (Instagram excluded for now) */}
                  <div className="inline-flex rounded-full  p-1 text-xs overflow-x-auto max-w-full gap-2">
                    {[
                      { id: 'instagram' as const, label: 'Instagram' },
                      { id: 'youtube' as const, label: 'YouTube' },
                      { id: 'vimeo' as const, label: 'Vimeo' },
                      { id: 'link' as const, label: 'Direct link' },
                    ].map((source) => (
                      <button
                        key={source.id}
                        type="button"
                        onClick={() => {
                          const nextSource = source.id as VideoSource;
                          setActiveSource(nextSource);
                          if (
                            nextSource === 'instagram' &&
                            instagramConnected &&
                            instagramVideos.length === 0 &&
                            !isLoadingInstagramVideos
                          ) {
                            void handleLoadInstagramVideos();
                          }
                        }}
                        className={cn(
                          'px-3 py-1.5 rounded-full transition-colors whitespace-nowrap',
                          activeSource === source.id
                            ? 'bg-[#F33363] text-white'
                            : 'text-[#F2F2F7] hover:bg-[#2C2C2E]',
                        )}
                      >
                        {source.label}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-2xl bg-[#151517] border border-[#3A3A3C] px-4 py-4 lg:px-5 lg:py-5 space-y-3">
                      {activeSource === 'instagram' && (
                        <>
                          <p className="text-sm font-semibold text-white">Import from Instagram</p>
                          <p className="text-xs text-[#8E8E93]">
                            {instagramConnected
                              ? 'Your Instagram account is connected. Your latest Instagram videos will appear here.'
                              : 'Connect your Instagram account to import your latest videos.'}
                          </p>
                          {!instagramConnected && (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <Button
                                type="button"
                                variant="secondary"
                                className="inline-flex items-center gap-2 px-4 py-2 text-xs lg:text-sm"
                                onClick={async () => {
                                  try {
                                    saveInstagramReturnState('videos');
                                    await InstagramAuthCore.startOAuth('videos', {
                                      returnPath: '/profile/gallery',
                                    });
                                  } catch (err) {
                                    console.error('Instagram videos OAuth error:', err);
                                    toast.error('Unable to start Instagram import. Please try again.');
                                  }
                                }}
                                disabled={isSubmitting || isLoadingInstagramVideos}
                              >
                                Connect Instagram
                              </Button>
                            </div>
                          )}
                          {instagramConnected && instagramVideos.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <p className="text-xs text-[#8E8E93] uppercase tracking-wider font-medium">
                                Select videos to import
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {instagramVideos.map((url) => {
                                  const selected = selectedInstagramVideos.has(url);
                                  return (
                                    <button
                                      key={url}
                                      type="button"
                                      onClick={() => handleToggleInstagramVideo(url)}
                                      className={cn(
                                        'relative aspect-video rounded-xl overflow-hidden border transition-all bg-black',
                                        selected
                                          ? 'border-[#F33363] ring-2 ring-[#F33363]/40'
                                          : 'border-[#3A3A3C]',
                                      )}
                                    >
                                      <video
                                        src={url}
                                        className="h-full w-full object-cover"
                                        controls={false}
                                        playsInline
                                        muted
                                      />
                                      {selected && (
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                          <span className="text-xs font-semibold text-white">
                                            Selected
                                          </span>
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  type="button"
                                  variant="active"
                                  className="px-6 py-2"
                                  onClick={handleImportSelectedInstagramVideos}
                                  disabled={isSubmitting || selectedInstagramVideos.size === 0}
                                >
                                  {isSubmitting ? 'Importing…' : 'Import selected'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {activeSource === 'youtube' && (
                        <>
                          <p className="text-sm font-semibold text-white">Search YouTube</p>
                          <p className="text-xs text-[#8E8E93]">
                            Search channels, pick a channel, then select videos to add.
                          </p>
                          {!selectedYouTubeChannelId && (
                            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                              <Input
                                placeholder="Search YouTube channels"
                                value={youtubeQuery}
                                onChange={(e) => setYoutubeQuery(e.target.value)}
                                className="bg-[#2C2C2E] sm:flex-1"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchYoutube();
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                variant="active"
                                className="w-full sm:w-auto px-4 py-2 text-xs lg:text-sm"
                                onClick={handleSearchYoutube}
                                disabled={videoSearchLoading}
                                isLoading={videoSearchLoading}
                              >
                                Search
                              </Button>
                            </div>
                          )}

                          {youtubeChannels.length > 0 && !selectedYouTubeChannelId && (
                            <div className="mt-3 space-y-3  overflow-y-auto custom-scrollbar">
                              <p className="text-[11px] text-[#8E8E93]">Select a channel:</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {youtubeChannels.map((ch) => {
                                  const thumbUrl = YouTube.getYouTubeThumbUrl(ch.thumbnails);
                                  return (
                                    <button
                                      key={ch.id}
                                      type="button"
                                      onClick={() => handleSelectYouTubeChannel(ch.id)}
                                      className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left bg-[#2C2C2E] border border-[#3A3A3C] hover:border-[#4A4A4C] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                      disabled={youtubeChannelLoadingId === ch.id || videoSearchLoading}
                                    >
                                      <div className="relative h-10 w-10 rounded-full bg-[#1C1C1E] border border-[#3A3A3C] overflow-hidden shrink-0">
                                        {thumbUrl && (
                                          <img
                                            src={thumbUrl}
                                            alt={ch.title}
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                            }}
                                          />
                                        )}
                                        {youtubeChannelLoadingId === ch.id && (
                                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                            <Loader size="small" />
                                          </div>
                                        )}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-xs text-[#E5E5EA] truncate">{ch.title}</p>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex items-center justify-between pt-1">
                                <button
                                  type="button"
                                  onClick={handleYouTubeChannelsPrevPage}
                                  disabled={youtubeChannelPage <= 1 || videoSearchLoading}
                                  className={cn(
                                    'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[4.5rem]',
                                    youtubeChannelPage <= 1 || videoSearchLoading
                                      ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                                      : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                                  )}
                                >
                                  <ChevronLeft className="h-3 w-3" />
                                  Prev
                                </button>
                                <span className="text-[11px] text-[#8E8E93]">
                                  Page {youtubeChannelPage}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleYouTubeChannelsNextPage}
                                  disabled={
                                    !youtubeChannelPageTokens[youtubeChannelPage - 1] || videoSearchLoading
                                  }
                                  className={cn(
                                    'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[4.5rem]',
                                    !youtubeChannelPageTokens[youtubeChannelPage - 1] || videoSearchLoading
                                      ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                                      : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                                  )}
                                >
                                  Next
                                  <ChevronRight className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )}
                          {selectedYouTubeChannelId && youtubeResults.length > 0 && (
                            <div className="mt-3 space-y-2 overflow-y-auto custom-scrollbar">
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedYouTubeChannelId(null);
                                    setYoutubeResults([]);
                                  }}
                                  className="text-[11px] text-[#F33363] hover:underline"
                                >
                                  ← Back to channels
                                </button>
                                <Button
                                  type="button"
                                  variant="active"
                                  className="px-3 py-1.5 text-[11px] lg:text-xs"
                                  onClick={() => handleAddSelectedFromResults('youtube')}
                                  disabled={isSubmitting}
                                  isLoading={isSubmitting}
                                >
                                  Add Selected Videos
                                </Button>
                              </div>
                              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
                                {youtubeResults.map((item) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => toggleResultSelection('youtube', item.id)}
                                    className={cn(
                                      'w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors',
                                      item.selected ? 'bg-[#F33363]/15 border border-[#F33363]' : 'bg-[#2C2C2E] border border-[#3A3A3C]'
                                    )}
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      {item.thumbnailUrl ? (
                                        <img
                                          src={item.thumbnailUrl}
                                          alt={item.title}
                                          className="h-10 w-16 rounded-md object-cover bg-[#1C1C1E] flex-shrink-0"
                                        />
                                      ) : (
                                        <div className="h-10 w-16 rounded-md bg-[#1C1C1E] border border-[#3A3A3C] flex items-center justify-center text-[10px] text-[#8E8E93] flex-shrink-0">
                                          No preview
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <p className="text-xs text-[#E5E5EA] truncate">{item.title}</p>
                                        <p className="text-[10px] text-[#8E8E93] truncate">{item.url}</p>
                                      </div>
                                    </div>
                                    <span
                                      className={cn(
                                        'inline-flex items-center justify-center h-5 w-5 rounded-md border-2',
                                        item.selected
                                          ? 'border-[#F33363] bg-[#F33363] text-white'
                                          : 'border-[#3A3A3C] bg-transparent text-transparent',
                                      )}
                                    >
                                      <Check className="h-3 w-4" />
                                    </span>
                                  </button>
                                ))}
                              </div>
                              <div className="flex items-center justify-between pt-2">
                                <button
                                  type="button"
                                  onClick={handleYouTubeVideosPrevPage}
                                  disabled={youtubeVideoPage <= 1 || videoSearchLoading}
                                  className={cn(
                                    'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[4.5rem]',
                                    youtubeVideoPage <= 1 || videoSearchLoading
                                      ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                                      : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                                  )}
                                >
                                  <ChevronLeft className="h-3 w-3" />
                                  Prev
                                </button>
                                <span className="text-[11px] text-[#8E8E93]">
                                  Page {youtubeVideoPage}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleYouTubeVideosNextPage}
                                  disabled={
                                    !youtubeVideoPageTokens[youtubeVideoPage - 1] || videoSearchLoading
                                  }
                                  className={cn(
                                    'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[4.5rem]',
                                    !youtubeVideoPageTokens[youtubeVideoPage - 1] || videoSearchLoading
                                      ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                                      : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                                  )}
                                >
                                  Next
                                  <ChevronRight className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {activeSource === 'vimeo' && (
                        <>
                          <p className="text-sm font-semibold text-white">Search Vimeo</p>
                          <p className="text-xs text-[#8E8E93]">
                            Find your Vimeo uploads and add them to your gallery.
                          </p>
                          {vimeoResults.length === 0 && (
                            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                              <Input
                                placeholder="Search Vimeo videos"
                                value={vimeoQuery}
                                onChange={(e) => setVimeoQuery(e.target.value)}
                                className="bg-[#2C2C2E] sm:flex-1"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchVimeo();
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                variant="active"
                                className="w-full sm:w-auto px-4 py-2 text-xs lg:text-sm"
                                onClick={handleSearchVimeo}
                                disabled={videoSearchLoading}
                                isLoading={videoSearchLoading}
                              >
                                Search
                              </Button>
                            </div>
                          )}
                          {vimeoResults.length > 0 && (
                            <div className="mt-3 space-y-2 overflow-y-auto custom-scrollbar">
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => setVimeoResults([])}
                                  className="text-[11px] text-[#F33363] hover:underline"
                                >
                                  ← Back to search
                                </button>
                                <Button
                                  type="button"
                                  variant="active"
                                  className="px-3 py-1.5 text-[11px] lg:text-xs"
                                  onClick={() => handleAddSelectedFromResults('vimeo')}
                                  disabled={isSubmitting}
                                  isLoading={isSubmitting}
                                >
                                  Add Selected Videos
                                </Button>
                              </div>
                              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
                                {vimeoResults.map((item) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => toggleResultSelection('vimeo', item.id)}
                                    className={cn(
                                      'w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors',
                                      item.selected ? 'bg-[#F33363]/15 border border-[#F33363]' : 'bg-[#2C2C2E] border border-[#3A3A3C]'
                                    )}
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      {item.thumbnailUrl ? (
                                        <img
                                          src={item.thumbnailUrl}
                                          alt={item.title}
                                          className="h-10 w-16 rounded-md object-cover bg-[#1C1C1E] flex-shrink-0"
                                        />
                                      ) : (
                                        <div className="h-10 w-16 rounded-md bg-[#1C1C1E] border border-[#3A3A3C] flex items-center justify-center text-[10px] text-[#8E8E93] flex-shrink-0">
                                          No preview
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <p className="text-xs text-[#E5E5EA] truncate">{item.title}</p>
                                        <p className="text-[10px] text-[#8E8E93] truncate">
                                          {item.url}
                                        </p>
                                      </div>
                                    </div>
                                    <span
                                      className={cn(
                                        'inline-flex items-center justify-center h-5 w-5 rounded-md border-2',
                                        item.selected
                                          ? 'border-[#F33363] bg-[#F33363] text-white'
                                          : 'border-[#3A3A3C] bg-transparent text-transparent',
                                      )}
                                    >
                                      <Check className="h-3 w-4" />
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {activeSource === 'link' && (
                        <>
                          <p className="text-sm font-semibold text-white">Add via direct link</p>
                          <p className="text-xs text-[#8E8E93]">
                            Paste a YouTube or Vimeo video URL.
                          </p>
                        </>
                      )}
                    </div>

                    {activeSource === 'link' && (
                      <div className="space-y-3">
                        <Input
                          label="Video URL"
                          placeholder="Paste YouTube or Vimeo video URL"
                          value={newVideoUrl}
                          onChange={(e) => setNewVideoUrl(e.target.value)}
                          className="bg-[#2C2C2E]"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddVideoLink();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="active"
                          className="w-full flex items-center justify-center gap-2"
                          onClick={handleAddVideoLink}
                          disabled={isSubmitting}
                        >
                          <Plus className="h-4 w-4" />
                          Add link
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Videos in Gallery – hidden when Add videos panel is open */}
            {!isVideoEditorOpen && (
              galleryVideosLoading ? (
                <div className="mt-4 pt-4 border-t border-[#3A3A3C]">
                  <p className="text-xs text-[#8E8E93]">Loading videos…</p>
                </div>
              ) : galleryVideos.length > 0 ? (
                <div className="mt-4 pt-4 border-t border-[#3A3A3C]">
                  <p className="mb-3 text-xs text-[#8E8E93] uppercase tracking-wider font-medium">Videos in Gallery</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {galleryVideos.map((video) => {
                      const kind = getVideoKind(video.url);
                      const embed = getEmbedUrl(video.url);
                      return (
                        <div
                          key={video.id}
                          className="relative rounded-2xl bg-[#151517] border border-[#3A3A3C] overflow-hidden group"
                        >
                          <div className="aspect-video w-full bg-black relative">
                            {kind === 'html5' ? (
                              <video
                                src={embed}
                                controls
                                playsInline
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <iframe
                                src={embed}
                                title="Performance video"
                                className="h-full w-full border-0 select-none"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                              />
                            )}
                          </div>

                          {videoModifyMode && (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteGalleryVideo(video.id, e)}
                              disabled={isSubmitting}
                              className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full bg-black/60 p-1.5 text-white hover:bg-[#FF453A] transition-colors z-10"
                              aria-label="Remove video"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}

                          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-3 pb-3 pt-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 px-2 py-0.5 text-[10px] text-white backdrop-blur-md">
                              <Play className="h-3 w-3 fill-white" />
                              Preview
                            </span>
                            <Link2 className="h-3.5 w-3.5 text-white/70" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null
            )}
            </div>
          </section>
        </div>
      </div>

      {/* Photo lightbox */}
      <AnimatePresence>
        {showLightbox && activePhotoIndex !== null && (
          <motion.div
            key="photo-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          >
            <button
              type="button"
              className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors z-[999]"
              onClick={() => setActivePhotoIndex(null)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center gap-4 px-4 w-full max-w-4xl h-full justify-center">
              <div className="relative w-full flex-1 flex items-center justify-center min-h-0">
                <button
                  type="button"
                  onClick={handlePrevPhoto}
                  className="absolute left-2 z-10 rounded-full bg-black/40 p-3 text-white hover:bg-black/60 backdrop-blur-md transition-all"
                  aria-label="Previous photo"
                >
                  ‹
                </button>
                <img
                  src={allPhotoUrls[activePhotoIndex]}
                  alt={`Photo ${activePhotoIndex + 1}`}
                  className="max-h-full max-w-full w-auto h-auto rounded-lg object-contain shadow-2xl"
                />
                <button
                  type="button"
                  onClick={handleNextPhoto}
                  className="absolute right-2 z-10 rounded-full bg-black/40 p-3 text-white hover:bg-black/60 backdrop-blur-md transition-all"
                  aria-label="Next photo"
                >
                  ›
                </button>
              </div>
              {allPhotoUrls.length > 1 && (
                <div className="flex max-w-full gap-2 overflow-x-auto pb-4 px-2 snap-x mb-24 md:mb-0">
                  {allPhotoUrls.map((url, index) => (
                    <button
                      key={galleryImages[index]?.id ?? url + index}
                      type="button"
                      onClick={() => setActivePhotoIndex(index)}
                      className={cn(
                        'h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border-2 snap-center transition-all',
                        index === activePhotoIndex ? 'border-[#F33363] scale-105' : 'border-transparent opacity-60 hover:opacity-100',
                      )}
                    >
                      <img src={url} alt={`Thumb ${index + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
